import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { captureEvent } from '~/analytics/posthog';
import { StandardJourneyBrandedScreen } from '~/components/StandardJourney';
import useStore from '~/store';
import PaymentConfirmExitDialog from './components/PaymentConfirmExitDialog';
import PaymentConfirmSuccessCard, { type PaymentConfirmFlowVariant } from './components/PaymentConfirmSuccessCard';

export default function PaymentConfirmPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [exitDialogOpen, setExitDialogOpen] = useState(false);

  const planParam = searchParams.get('plan');
  const distribuitorParam = searchParams.get('dis');
  const orgParam = searchParams.get('org');
  const sellerParam = searchParams.get('seller');

  const { contractResponse, promotionData, products } = useStore((store) => ({
    contractResponse: store.contractResponse,
    promotionData: store.promotionData,
    products: store.products
  }));

  const isOnlyGuarantee = products?.length === 1 && String(products[0]?.product_id) === '9';
  const isPromotionFlow = promotionData?.has_promotion === true && promotionData?.is_promotion_available === true;

  const flowVariant: PaymentConfirmFlowVariant = isPromotionFlow
    ? 'promotion'
    : isOnlyGuarantee
    ? 'guarantee'
    : 'payment';

  const contractCode = useMemo(() => {
    const response = contractResponse as { contract_code?: string; contract_id?: string } | null | undefined;
    if (response?.contract_code) return String(response.contract_code);
    if (response?.contract_id) return String(response.contract_id);
    return '';
  }, [contractResponse]);

  useEffect(() => {
    captureEvent('payment_approved', { contract_id: contractCode || undefined, variant: flowVariant });
  }, [contractCode, flowVariant]);

  const navigateToContract = useCallback(() => {
    const params = new URLSearchParams();
    const response = contractResponse as { contract_id?: string } | null | undefined;
    params.set('cid', String(response?.contract_id ?? ''));
    params.set('source', 'email');
    if (orgParam) params.set('org', orgParam);
    navigate(`/contract/?${params.toString()}`);
  }, [contractResponse, navigate, orgParam]);

  const navigateHome = useCallback(() => {
    const queryTail = `${distribuitorParam ? `&dis=${distribuitorParam}` : ''}${orgParam ? `&org=${orgParam}` : ''}${
      sellerParam ? `&seller=${sellerParam}` : ''
    }`;
    const path = planParam
      ? `/?plan=${planParam}${queryTail}`
      : `/${queryTail ? `?${queryTail.replace(/^&/, '')}` : ''}`;
    navigate(path);
  }, [distribuitorParam, navigate, orgParam, planParam, sellerParam]);

  const handleBackAttempt = useCallback(() => {
    setExitDialogOpen(true);
  }, []);

  return (
    <StandardJourneyBrandedScreen
      appId="journey-payment-confirm"
      checkoutActiveStep={2}
      contentLayout="checkout"
      onBack={handleBackAttempt}
      progressPercent={100}
      showBack
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <PaymentConfirmSuccessCard
          onFillContract={navigateToContract}
          // Sai para o mesmo aviso do header: nunca abandonar o fluxo sem confirmar.
          onFinishLater={handleBackAttempt}
          variant={flowVariant}
        />
      </Box>

      <PaymentConfirmExitDialog
        onClose={() => setExitDialogOpen(false)}
        onConfirmExit={() => {
          setExitDialogOpen(false);
          navigateHome();
        }}
        onFillContract={() => {
          setExitDialogOpen(false);
          navigateToContract();
        }}
        open={exitDialogOpen}
      />
    </StandardJourneyBrandedScreen>
  );
}
